import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Pipe({
  name: 'transliterate',
  pure: false
})
export class TransliteratePipe implements PipeTransform {
  constructor(private translate: TranslateService) {}

  transform(value: string, targetLang?: string): string {
    if (!value || typeof value !== 'string') return value;

    const lang = targetLang || this.translate.currentLang || localStorage.getItem('lang') || 'en-US';
    if (lang === 'en-US') {
      return value;
    }

    if (lang === 'hi-IN' || lang === 'mr-IN') {
      return this.transliterateToDevanagari(value, lang);
    } else if (lang === 'bn-IN') {
      return this.transliterateToBengali(value);
    }

    return value;
  }

  private transliterateToDevanagari(text: string, lang: string): string {
    const htmlRegex = /(<[^>]+>|https?:\/\/[^\s]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const tokens = text.split(htmlRegex);

    return tokens.map(token => {
      if (htmlRegex.test(token) || token.startsWith('<') || token.startsWith('http')) {
        return token;
      }
      return this.convertDevanagariWords(token, lang);
    }).join('');
  }

  private transliterateToBengali(text: string): string {
    const htmlRegex = /(<[^>]+>|https?:\/\/[^\s]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
    const tokens = text.split(htmlRegex);

    return tokens.map(token => {
      if (htmlRegex.test(token) || token.startsWith('<') || token.startsWith('http')) {
        return token;
      }
      return this.convertBengaliWords(token);
    }).join('');
  }

  private convertDevanagariWords(str: string, lang: string): string {
    const isMarathi = lang === 'mr-IN';
    const dict: { [key: string]: string } = {
      "captech": "कैपटेक",
      "eforce": "ई-फोर्स",
      "website": "वेबसाइट",
      "application": "एप्लिकेशन",
      "site": "साइट",
      "privacy": "प्राइवेसी",
      "policy": "पॉलिसी",
      "terms": "टर्म्स",
      "conditions": "कंडीशंस",
      "user": "यूज़र",
      "users": "यूज़र्स",
      "user's": "यूज़र का",
      "agreement": "एग्रीमेंट",
      "service": "सर्विस",
      "services": "सर्विसेज",
      "software": "सॉफ्टवेयर",
      "registration": "रजिस्ट्रेशन",
      "cancellation": "कैंसिलेशन",
      "information": "इन्फॉर्मेशन",
      "company": "कंपनी",
      "contact": "कांटेक्ट",
      "support": "सपोर्ट",
      "notice": "नोटिस",
      "legal": "लीगल",
      "rights": "राइट्स",
      "obligations": "ऑब्लिगेशंस",
      "content": "कंटेंट",
      "limitation": "लिमिटेशन",
      "liability": "लायबिलिटी",
      "termination": "टर्मिनेशन",
      "general": "जनरल",
      "spam": "स्पैम",
      "unsolicited": "अनसॉलिसिटेड",
      "emails": "ई-मेल्स",
      "email": "ई-मेल",
      "indemnification": "इंडेम्निफिकेशन",
      "severability": "सेवरेबिलिटी",
      "waiver": "वेवर",
      "disclaimer": "डिस्क्लेमर",
      "copyright": "कॉपीराइट",
      "trademarks": "ट्रेडमार्क्स",
      "trademark": "ट्रेडमार्क",
      "feedback": "फीडबैक",
      "comments": "कमेंट्स",
      "jurisdiction": "ज्यूरिस्डिक्शन",
      "governed": "गवर्न्ड",
      "provision": "प्रोविज़न",
      "provisions": "प्रोविज़न्स",
      "law": "लॉ",
      "laws": "लॉज़",
      "indian": "इंडियन",
      "india": "इंडिया",
      "mumbai": "मुंबई",
      "aws": "एडब्ल्यूएस",
      "party": "पार्टी",
      "parties": "पार्टीज",
      "third": "थर्ड",
      "3rd": "3र्ड",
      "access": "एक्सेस",
      "account": "अकाउंट",
      "personal": "पर्सनल",
      "cookies": "कुकीज़",
      "data": "डेटा",
      "material": "मटेरियल",
      "materials": "मटेरियल्स",
      "damages": "डैमेजेस",
      "loss": "लॉस",
      "claims": "क्लेम्स",
      "expenses": "एक्सपेंसेस",
      "prohibited": "प्रोहिबिटेड",
      "violation": "वायलेशन",
      "violations": "वायलेशन्स",
      "security": "सिक्योरिटी",
      "network": "नेटवर्क",
      "networks": "नेटवर्क्स",
      "system": "सिस्टम",
      "systems": "सिस्टम्स",
      "delete": "डिलीट",
      "deleted": "डिलीटेड",
      "cancel": "कैंसिल",
      "cancelling": "कैंसलिंग",
      "suspended": "सस्पेंडेड",
      "terminate": "टर्मिनेट",
      "terminated": "टर्मिनेटेड"
    };

    return str.replace(/[a-zA-Z']+/g, (word) => {
      const lower = word.toLowerCase();
      if (dict[lower]) {
        return dict[lower];
      }
      return this.phoneticDevanagari(word, isMarathi);
    });
  }

  private convertBengaliWords(str: string): string {
    const dict: { [key: string]: string } = {
      "captech": "ক্যাপটেক",
      "eforce": "ই-ফোর্স",
      "website": "ওয়েবসাইট",
      "application": "অ্যাপ্লিকেশন",
      "site": "সাইট",
      "privacy": "প্রাইভেসি",
      "policy": "পলিসি",
      "terms": "টার্মস",
      "conditions": "কন্ডিশনস",
      "user": "ইউজার",
      "users": "ইউজারস",
      "user's": "ইউজারের",
      "agreement": "এগ্রিমেন্ট",
      "service": "সার্ভিস",
      "services": "সার্ভিসেস",
      "software": "সফটওয়্যার",
      "registration": "রেজিস্ট্রেশন",
      "cancellation": "ক্যানসেলেশন",
      "information": "ইনফরমেশন",
      "company": "কোম্পানি",
      "contact": "কন্ট্যাক্ট",
      "support": "সাপোর্ট",
      "notice": "নোটিশ",
      "legal": "লিগ্যাল",
      "rights": "রাইটস",
      "obligations": "অবিলগেশনস",
      "content": "কন্টেন্ট",
      "limitation": "লিমিটেশন",
      "liability": "লায়াবিলিটি",
      "termination": "টার্মিনেশন",
      "general": "জেনারেল",
      "spam": "স্প্যাম",
      "unsolicited": "আনসলিসিটেড",
      "emails": "ই-মেলস",
      "email": "ই-মেল",
      "indemnification": "ইনডেমনিফিকেশন",
      "severability": "সেভরেবিলিটি",
      "waiver": "ওয়েভার",
      "disclaimer": "ডিসক্লেইমার",
      "copyright": "কপিরাইট",
      "trademarks": "ট্রেডমার্কস",
      "trademark": "ট্রেডমার্ক",
      "feedback": "ফিডব্যাক",
      "comments": "কমেন্টস",
      "jurisdiction": "জুরিসডিকশন",
      "governed": "গভর্নড",
      "provision": "প্রভিশন",
      "provisions": "প্রভিশনস",
      "law": "ল",
      "laws": "লস",
      "indian": "ইন্ডিয়ান",
      "india": "ইন্ডিয়া",
      "mumbai": "মুম্বাই",
      "aws": "এডাব্লিউএস",
      "party": "পার্টি",
      "parties": "পার্টিস",
      "third": "থার্ড",
      "3rd": "৩র্ড",
      "access": "এক্সেস",
      "account": "অ্যাকাウント",
      "personal": "পার্সোনাল",
      "cookies": "কুকিজ",
      "data": "ডাটা",
      "material": "মেটেরিয়াল",
      "materials": "মেটেরিয়ালস",
      "damages": "ড্যামেজেস",
      "loss": "লস",
      "claims": "ক্লেমস",
      "expenses": "এক্সপেনসেস",
      "prohibited": "প্রহিবিটেড",
      "violation": "ভায়োলেশন",
      "violations": "ভায়োলেশনস",
      "security": "সিকিউরিটি",
      "network": "নেটওয়ার্ক",
      "networks": "নেটওয়ার্কস",
      "system": "সিস্টেম",
      "systems": "সিস্টেমস",
      "delete": "ডিলিট",
      "deleted": "ডিলিটেড",
      "cancel": "ক্যানসেল",
      "cancelling": "ক্যানসেলিং",
      "suspended": "সাসপেন্ডেড",
      "terminate": "টার্মিনেট",
      "terminated": "টার্মিনেটেড"
    };

    return str.replace(/[a-zA-Z']+/g, (word) => {
      const lower = word.toLowerCase();
      if (dict[lower]) {
        return dict[lower];
      }
      return this.phoneticBengali(word);
    });
  }

  private phoneticDevanagari(w: string, isMarathi: boolean): string {
    let s = w.toLowerCase();
    s = s.replace(/tion/g, 'शन').replace(/sion/g, 'शन').replace(/ment/g, 'मेंट')
         .replace(/ing/g, 'इंग').replace(/th/g, 'थ').replace(/sh/g, 'श')
         .replace(/ch/g, 'च').replace(/ph/g, 'फ').replace(/wh/g, 'व्ह')
         .replace(/ck/g, 'क').replace(/qu/g, 'क्व').replace(/ee/g, 'ी')
         .replace(/oo/g, 'ू').replace(/ea/g, 'ी').replace(/ai/g, 'ै')
         .replace(/ou/g, 'ू').replace(/or/g, 'ऑर').replace(/er/g, 'र')
         .replace(/ar/g, 'ार');

    const devMap: { [k: string]: string } = {
      'a': 'ा', 'e': 'े', 'i': 'ि', 'o': 'ो', 'u': 'ु',
      'b': 'ब', 'c': 'क', 'd': 'ड', 'f': 'फ', 'g': 'ग',
      'h': 'ह', 'j': 'ज', 'k': 'क', 'l': 'ल', 'm': 'म',
      'n': 'न', 'p': 'प', 'r': 'र', 's': 'स', 't': 'ट',
      'v': 'व', 'w': 'व', 'x': 'क्स', 'y': 'य', 'z': 'ज़'
    };

    let result = '';
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (devMap[ch]) {
        result += devMap[ch];
      } else {
        result += ch;
      }
    }
    return result;
  }

  private phoneticBengali(w: string): string {
    let s = w.toLowerCase();
    s = s.replace(/tion/g, 'শন').replace(/sion/g, 'শন').replace(/ment/g, 'মেন্ট')
         .replace(/ing/g, 'ইং').replace(/th/g, 'থ').replace(/sh/g, 'শ')
         .replace(/ch/g, 'চ').replace(/ph/g, 'ফ').replace(/wh/g, 'হো')
         .replace(/ck/g, 'ক').replace(/qu/g, 'কোয়া').replace(/ee/g, 'ী')
         .replace(/oo/g, 'ূ').replace(/ea/g, 'ী').replace(/ai/g, 'ৈ')
         .replace(/ou/g, 'ূ').replace(/or/g, 'অর').replace(/er/g, 'র')
         .replace(/ar/g, 'ার');

    const bnMap: { [k: string]: string } = {
      'a': 'া', 'e': 'ে', 'i': 'ি', 'o': 'ো', 'u': 'ু',
      'b': 'ব', 'c': 'ক', 'd': 'ড', 'f': 'ফ', 'g': 'গ',
      'h': 'হ', 'j': 'জ', 'k': 'ক', 'l': 'ল', 'm': 'ম',
      'n': 'ন', 'p': 'প', 'r': 'র', 's': 'স', 't': 'ট',
      'v': 'ব', 'w': 'ওয়', 'x': 'ক্স', 'y': 'য়', 'z': 'জ'
    };

    let result = '';
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (bnMap[ch]) {
        result += bnMap[ch];
      } else {
        result += ch;
      }
    }
    return result;
  }
}
